/**
 * RFQ Create Wizard - CR-1403
 *
 * 6-step wizard for creating RFQs:
 * 1. Basic Details
 * 2. Line Items
 * 3. Additional Costs
 * 4. Documents & NDA
 * 5. Supplier Selection
 * 6. Review & Send
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, ArrowRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI, suppliersAPI, fetchAllPages } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useAutoSave } from '../../hooks/useAutoSave';

import {
  STEPS,
  EMPTY_LINE_ITEM,
  EMPTY_COST,
  EMPTY_CERTIFICATE,
  DEFAULT_BASIC_DETAILS,
} from './rfqWizardTypes';
import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
  SupplierOption,
} from './rfqWizardTypes';
import {
  BasicDetailsStep,
  LineItemsStep,
  AdditionalCostsStep,
  DocumentsStep,
  SupplierSelectionStep,
  ReviewStep,
} from './RFQWizardSteps';
import type { RFQTypeOption } from './RFQWizardSteps';
import { WizardStepIndicator, WizardSaveStatus, WizardResumePrompt } from './RFQWizardChrome';
import { createRfqFromWizard } from './rfqWizardSubmit';
import { validateWizardStep, NDA_MISSING_MESSAGE } from './rfqWizardValidation';
import type { RFQType, SupplierProfile } from '../../lib/api';

/** CR-5461: shape of the wizard state we persist server-side as a draft.
 *  Loose type — JSONField on the backend stores it as-is, validation is
 *  at submit. Keep in sync with the form state shapes above. */
interface RFQWizardDraftState {
  basicDetails: BasicDetailsForm;
  lineItems: LineItemForm[];
  additionalCosts: AdditionalCostForm[];
  requiredCertificates: RequiredCertificateForm[];
  selectedSuppliers: number[];
  currentStep: number;
}

interface RFQCreateWizardProps {
  onClose: () => void;
  onCreate?: () => void;
}

export default function RFQCreateWizard({ onClose, onCreate }: RFQCreateWizardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The RFQ is raised in the customer's reporting currency (site → company →
  // platform cascade), not a hard-coded GBP — an INR tenant quotes in INR.
  const { preferences } = useFormatters();
  const rfqCurrency = preferences.currency || 'GBP';
  // CR-5468 rework: a Helix admin has no customer of their own, and the
  // backend (rightly) refuses to guess one — it requires the RFQ to name the
  // customer it is raised on behalf of, so one customer can't be spoofed as
  // another. The wizard never sent it, so every admin attempt died on the
  // final step with "Failed to create RFQ", which is what QA hit. Take the
  // customer from the company the admin already has selected in the top bar.
  const { selectedCompanyId } = useView();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  // CR-5466: admin-managed dropdown values for the Basic Details step.
  const [rfqTypes, setRfqTypes] = useState<RFQTypeOption[]>([]);

  // Step 1: Basic Details
  const [basicDetails, setBasicDetails] = useState<BasicDetailsForm>({ ...DEFAULT_BASIC_DETAILS });

  // Step 2: Line Items
  const [lineItems, setLineItems] = useState<LineItemForm[]>([{ ...EMPTY_LINE_ITEM }]);

  // Step 3: Additional Costs
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCostForm[]>([]);

  // Step 4: Documents & Certificates
  const [requiredCertificates, setRequiredCertificates] = useState<RequiredCertificateForm[]>([]);

  // Step 5: Selected Suppliers
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);
  // The NDA file for an NDA-required RFQ. Deliberately NOT part of the
  // server-side draft (a File can't be serialised) — a resumed draft asks
  // for it again on the Certificates & NDA step.
  const [ndaFile, setNdaFile] = useState<File | null>(null);

  // CR-5461: server-side draft auto-save state.
  //  draftLoading  - true while the initial GET /drafts/me/ is in flight
  //  resumeDraft   - non-null when an existing draft was found; showing the
  //                  "Resume / Start Fresh" prompt blocks auto-save so the
  //                  empty default state doesn't overwrite the user's draft.
  const [draftLoading, setDraftLoading] = useState(true);
  const [resumeDraft, setResumeDraft] = useState<{ state: RFQWizardDraftState; updatedAt: string } | null>(null);

  useEffect(() => {
    fetchSuppliers();
    fetchDraft();
    fetchRfqTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      // `minimal` keeps the payload to name + ids (the full profile list is
      // >1 MB for a few hundred suppliers); walk pages so the backend's
      // page_size clamp can't silently truncate the list.
      const list = await fetchAllPages<SupplierProfile>((page) =>
        suppliersAPI.getProfiles({ page_size: 200, minimal: true, page }),
      );
      setSuppliers(list.map((p) => ({
        id: p.id,
        company: p.company ?? p.company_id,
        company_name: p.company_name,
      })));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setSuppliersLoading(false);
    }
  };

  // CR-5466: load the admin-managed RFQ Type dropdown values.
  const fetchRfqTypes = async () => {
    try {
      // Walk pages — the backend clamps page_size, so one request silently truncated (also absorbs the flat-list envelope shape).
      const list = await fetchAllPages<RFQType>((page) => rfqAPI.getRFQTypes({ page_size: 100, page }));
      setRfqTypes(list.map((t) => ({ id: t.id, name: t.name, enabled: t.enabled })));
    } catch (error) {
      console.warn('[CR-5466] failed to fetch RFQ types', error);
      setRfqTypes([]);
    }
  };

  // CR-5461: load any server-persisted draft on wizard mount. If one
  // exists, show the resume banner BEFORE enabling auto-save so the
  // initial empty form state doesn't clobber it.
  const fetchDraft = async () => {
    try {
      const response = await rfqAPI.getDraft();
      if (response.status === 204 || !response.data) {
        setResumeDraft(null);
      } else {
        // Double-cast: wizard_state is Record<string, unknown> on the wire
        // (the backend stores it as opaque JSON) but the wizard re-hydrates
        // it into the specific RFQWizardDraftState shape it owns. CLAUDE.md
        // rule for incompatible types: `as unknown as TargetType`.
        const data = response.data as unknown as { wizard_state: RFQWizardDraftState; updated_at: string };
        setResumeDraft({ state: data.wizard_state, updatedAt: data.updated_at });
      }
    } catch (error) {
      // If the draft fetch fails (network blip, server error) we don't
      // block the wizard — user can keep working, auto-save will retry.
      console.warn('[CR-5461] failed to fetch RFQ draft', error);
      setResumeDraft(null);
    } finally {
      setDraftLoading(false);
    }
  };

  // CR-5461: combine all 5 form states + step into one object so the
  // auto-save hook has a single value to watch and persist.
  const wizardState = useMemo<RFQWizardDraftState>(
    () => ({ basicDetails, lineItems, additionalCosts, requiredCertificates, selectedSuppliers, currentStep }),
    [basicDetails, lineItems, additionalCosts, requiredCertificates, selectedSuppliers, currentStep],
  );

  // Auto-save is enabled once the resume prompt is dismissed (either by
  // resuming or starting fresh) and the initial draft GET has settled.
  const autoSaveEnabled = !draftLoading && resumeDraft === null;
  const { status: saveStatus, lastSavedAt } = useAutoSave(
    wizardState,
    async (state) => { await rfqAPI.saveDraft(state as unknown as Record<string, unknown>); },
    3000,
    autoSaveEnabled,
  );

  const handleResumeDraft = () => {
    if (!resumeDraft) return;
    const s = resumeDraft.state;
    // Defensive: fall back to defaults if any field is missing from the
    // stored shape (older draft saved before a wizard field was added).
    if (s.basicDetails) setBasicDetails({ ...DEFAULT_BASIC_DETAILS, ...s.basicDetails });
    if (Array.isArray(s.lineItems) && s.lineItems.length > 0) setLineItems(s.lineItems);
    if (Array.isArray(s.additionalCosts)) setAdditionalCosts(s.additionalCosts);
    if (Array.isArray(s.requiredCertificates)) setRequiredCertificates(s.requiredCertificates);
    if (Array.isArray(s.selectedSuppliers)) setSelectedSuppliers(s.selectedSuppliers);
    if (typeof s.currentStep === 'number') setCurrentStep(Math.max(1, Math.min(6, s.currentStep)));
    setResumeDraft(null);
    toast.success('Draft resumed');
  };

  const handleDiscardDraft = async () => {
    try {
      await rfqAPI.deleteDraft();
    } catch (error) {
      console.warn('[CR-5461] failed to delete RFQ draft', error);
    }
    setResumeDraft(null);
  };

  const calculateTotalValue = () => {
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      const price = item.target_unit_price || 0;
      return sum + (price * item.quantity);
    }, 0);
    const costsTotal = additionalCosts.reduce((sum, cost) => sum + cost.amount, 0);
    return lineItemsTotal + costsTotal;
  };

  const validateStep = (step: number): boolean =>
    validateWizardStep(step, { basicDetails, lineItems, ndaFile });

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    } else if (currentStep === 4) {
      toast.error(NDA_MISSING_MESSAGE);
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const updateLineItem = (index: number, field: keyof LineItemForm, value: string | number | null) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const updateAdditionalCost = (index: number, field: keyof AdditionalCostForm, value: string | number) => {
    const updated = [...additionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setAdditionalCosts(updated);
  };

  const updateCertificate = (index: number, field: keyof RequiredCertificateForm, value: string | number | boolean) => {
    const updated = [...requiredCertificates];
    updated[index] = { ...updated[index], [field]: value };
    setRequiredCertificates(updated);
  };

  const toggleSupplier = (companyId: number) => {
    setSelectedSuppliers(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleSubmit = async (sendToSuppliers: boolean = false) => {
    try {
      setLoading(true);

      if (!user?.company) {
        toast.error('User company not found. Please log in again.');
        return;
      }

      // Helix admins must say which customer the RFQ belongs to. The top-bar
      // company selector is that choice — when it is on "All Companies" there
      // is nothing to infer, so say so here rather than letting the request
      // fail with a validation error the user can't act on.
      const isHelixAdmin = user.company_type === 'helix';
      if (isHelixAdmin && !selectedCompanyId) {
        toast.error('Select the customer company in the top bar before raising an RFQ on their behalf.');
        return;
      }

      // Belt and braces with validateStep(4): the Review step's "Send" can be
      // reached on a resumed draft whose File was never re-picked.
      if (sendToSuppliers && basicDetails.nda_required && !ndaFile) {
        toast.error(NDA_MISSING_MESSAGE);
        setCurrentStep(4);
        return;
      }

      const { rfqId, sent } = await createRfqFromWizard({
        // Sent only for Helix admins; for a customer user the backend stamps
        // the active company itself and ignores anything sent here.
        customerId: isHelixAdmin && selectedCompanyId ? selectedCompanyId : undefined,
        currency: rfqCurrency,
        basicDetails,
        lineItems,
        additionalCosts,
        requiredCertificates,
        ndaFile,
        supplierIds: selectedSuppliers,
        sendToSuppliers,
      });
      toast.success(sent ? 'RFQ created and sent to suppliers' : 'RFQ created as draft');

      // CR-5461: clear the wizard draft on successful submit so the user
      // doesn't get a stale "Resume draft?" banner next time. Best-effort —
      // the RFQ is already created server-side, so a failed delete here
      // only means the banner appears once on the next visit.
      try {
        await rfqAPI.deleteDraft();
      } catch (error) {
        console.warn('[CR-5461] failed to clear RFQ draft post-submit', error);
      }

      onCreate?.();
      onClose();
      navigate(`/rfq/${rfqId}`);
    } catch (error) {
      console.error('Error creating RFQ:', error);
      // CR-5468: surface the backend's actual validation message so users
      // get an actionable error instead of the generic "Failed to create
      // RFQ" toast that hid the real cause for weeks.
      const msg = getApiErrorMessage(error, 'Failed to create RFQ');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // CR-5466: read-only display values for the redesigned Basic Details step.
  const customerName = user?.companyName ?? '';
  const raisedByName = user?.full_name?.trim() || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.email || '';

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicDetailsStep
            basicDetails={basicDetails}
            setBasicDetails={setBasicDetails}
            rfqTypes={rfqTypes}
            customerName={customerName}
            raisedByName={raisedByName}
          />
        );
      case 2:
        return (
          <LineItemsStep
            lineItems={lineItems}
            onAdd={() => setLineItems([...lineItems, { ...EMPTY_LINE_ITEM }])}
            onRemove={(index) => { if (lineItems.length > 1) setLineItems(lineItems.filter((_, i) => i !== index)); }}
            onUpdate={updateLineItem}
          />
        );
      case 3:
        return (
          <AdditionalCostsStep
            costs={additionalCosts}
            currency={rfqCurrency}
            onAdd={() => setAdditionalCosts([...additionalCosts, { ...EMPTY_COST }])}
            onRemove={(index) => setAdditionalCosts(additionalCosts.filter((_, i) => i !== index))}
            onUpdate={updateAdditionalCost}
          />
        );
      case 4:
        return (
          <DocumentsStep
            certificates={requiredCertificates}
            ndaRequired={basicDetails.nda_required}
            ndaFile={ndaFile}
            onNdaFileChange={setNdaFile}
            onAdd={() => setRequiredCertificates([...requiredCertificates, { ...EMPTY_CERTIFICATE }])}
            onRemove={(index) => setRequiredCertificates(requiredCertificates.filter((_, i) => i !== index))}
            onUpdate={updateCertificate}
          />
        );
      case 5:
        return (
          <SupplierSelectionStep
            suppliers={suppliers}
            selectedSuppliers={selectedSuppliers}
            onToggle={toggleSupplier}
            loading={suppliersLoading}
          />
        );
      case 6:
        return (
          <ReviewStep
            basicDetails={basicDetails}
            ndaFile={ndaFile}
            lineItems={lineItems}
            additionalCosts={additionalCosts}
            requiredCertificates={requiredCertificates}
            selectedSuppliers={selectedSuppliers}
            suppliers={suppliers}
            totalValue={calculateTotalValue()}
            rfqTypes={rfqTypes}
            currency={rfqCurrency}
          />
        );
      default:
        return null;
    }
  };

  // CR-5461: blocking resume prompt when an existing draft was found.
  if (resumeDraft) {
    return (
      <WizardResumePrompt
        updatedAt={resumeDraft.updatedAt}
        onClose={onClose}
        onResume={handleResumeDraft}
        onDiscard={handleDiscardDraft}
      />
    );
  }

  return (
    <div className="bg-background min-h-[calc(100vh-200px)]">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Create New RFQ</h1>
            <WizardSaveStatus enabled={autoSaveEnabled} status={saveStatus} lastSavedAt={lastSavedAt} />
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <WizardStepIndicator currentStep={currentStep} />

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
            <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderCurrentStep()}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={currentStep === 1 ? onClose : handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>

          <div className="flex gap-2">
            {currentStep === 6 ? (
              <>
                {/* CR-5468 rework: this creates the RFQ record (status
                    draft) — distinct from the wizard progress the pill above
                    auto-saves. Named for what it does so the two stop
                    reading as the same action. */}
                <Button variant="outline" onClick={() => handleSubmit(false)} disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create as draft RFQ
                </Button>
                {selectedSuppliers.length > 0 && (
                  <Button onClick={() => handleSubmit(true)} disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create & Send
                  </Button>
                )}
              </>
            ) : (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
